/**
 * @file components/audit-tasks/lab/LabWaiverCard.tsx
 * @description 「檢驗需求」卡片：標記/撤銷「本案無需檢驗」（檢驗豁免）。
 *
 * - 未標記：說明 + 「標記本案無需檢驗」按鈕（Popconfirm 內可填原因）。
 * - 已標記：Alert 顯示標記人/時間/原因 + 「撤銷標記」按鈕，提示可直接前往報告編製。
 * - 權限：管理組（case.dispatch）或本案主導稽核員；其餘只讀。
 * - 標記僅為流程語義（階段導航顯示「已跳過」），不鎖任何操作，檢驗資料仍可補錄。
 */

import { Alert, Button, Card, Input, Popconfirm, Space, Typography } from 'antd';
import { FlaskConical } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userCan, type AuditTaskDetail } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { message } from '@/lib/antd-app';
import { useAuthStore } from '@/lib/auth-context';

interface Props {
  task: AuditTaskDetail;
}

export function LabWaiverCard({ task }: Props) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [reason, setReason] = useState('');

  const waived = task.labWaivedAt != null;
  // 授權口徑與後端一致：管理組（case.dispatch）或本案主導稽核員
  const canToggle = user
    ? userCan(user, 'case.dispatch') || user.id === task.caseAssignedTeamLeadId
    : false;

  const mutation = useMutation({
    mutationFn: (input: { waived: boolean; reason?: string }) =>
      apiClient.patch(`/api/cases/${task.caseId}/lab-waiver`, input),
    onSuccess: (_res, vars) => {
      void message.success(vars.waived ? '已標記本案無需檢驗' : '已撤銷標記');
      setReason('');
      void qc.invalidateQueries({ queryKey: ['audit-task-detail', task.id] });
    },
    onError: (e: Error) => {
      console.error('[LabWaiverCard] set lab waiver failed', e);
      void message.error(e.message || '操作失敗');
    },
  });

  return (
    <Card
      size="small"
      title={
        <Space size={6}>
          <FlaskConical size={14} />
          <span>檢驗需求</span>
        </Space>
      }
    >
      {waived ? (
        <Alert
          type="info"
          showIcon
          message="本案已標記無需檢驗，本階段視為已跳過"
          description={
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {task.labWaivedByName ?? '—'} 於 {new Date(task.labWaivedAt!).toLocaleString()} 標記
                {task.labWaivedReason ? `；原因：${task.labWaivedReason}` : ''}
                。可直接前往「報告編製」階段。
              </Typography.Text>
              {canToggle && (
                <Popconfirm
                  title="撤銷「無需檢驗」標記？"
                  description="撤銷後本階段恢復為正常檢驗流程。"
                  onConfirm={() => mutation.mutate({ waived: false })}
                >
                  <Button size="small" danger loading={mutation.isPending}>
                    撤銷標記
                  </Button>
                </Popconfirm>
              )}
            </Space>
          }
        />
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            客戶無產品檢驗需求時，可標記「本案無需檢驗」跳過本階段、直接進行報告編製；
            標記可隨時撤銷，已上傳的檢驗資料不受影響。
          </Typography.Text>
          {canToggle ? (
            <Popconfirm
              title="標記本案無需檢驗？"
              description={
                <Input.TextArea
                  rows={2}
                  maxLength={500}
                  placeholder="原因（選填），例如：客戶產品無抽驗項目"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ width: 260 }}
                />
              }
              onConfirm={() =>
                mutation.mutate({ waived: true, reason: reason.trim() || undefined })
              }
            >
              <Button size="small" loading={mutation.isPending}>
                標記本案無需檢驗
              </Button>
            </Popconfirm>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              （僅管理組或本案主導稽核員可標記）
            </Typography.Text>
          )}
        </Space>
      )}
    </Card>
  );
}
